import { useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaCopy, FaEdit, FaFolderOpen, FaPlus, FaShareAlt, FaTable, FaTrash } from "react-icons/fa";
import Modal from "../../components/Modal";
import Spinner from "../../components/Spinner";
import { LdsOverviewEmpty, LdsOverviewHero, LdsOverviewPage } from "../../components/lds/LdsOverview";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../../components/lds/LdsTable";
import { LdsBadge } from "../../components/lds/LdsBadge";
import { useCan } from "../../authz/useCan";
import { formatDutchDate } from "../../utils/dutchDate";
import { createReport, deleteReport, fetchLibrary, fetchMyReports, shareReport } from "./api";
import { reportErrorMessage } from "./errors";
import { dangerButton, fitWidth, ghostButton, primaryButton } from "./components/styles";
import type { SavedReport } from "./types";

type Tab = "mine" | "library";

/** "My reports" and the school's library of shared reports. */
export default function ReportBuilderListPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canCreate = useCan("reports.builder", "create");
  // Without the right to make reports, only the library (read-only) is available.
  const tab: Tab = !canCreate || searchParams.get("tab") === "library" ? "library" : "mine";
  const canShare = useCan("reports.builder", "share");
  const [reports, setReports] = useState<SavedReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<SavedReport | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (tab === "library" ? fetchLibrary() : fetchMyReports())
      .then((data) => { if (!cancelled) setReports(data); })
      .catch((caught) => { if (!cancelled) setError(reportErrorMessage(t, caught)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [t, tab]);

  const sourceName = (source: string) => String(t(`reports.builder.sources.${source}.label`, {
    defaultValue: ({ children: "Children", families: "Families", classrooms: "Classes", schoolYears: "School years" } as
      Record<string, string>)[source] ?? source,
  }));

  const act = async (action: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(reportErrorMessage(t, caught));
    } finally {
      setBusy(false);
    }
  };

  const duplicate = (report: SavedReport) => act(async () => {
    const copy = await createReport({
      name: String(t("reports.builder.copy_name", { name: report.name, defaultValue: "Copy of {{name}}" })).slice(0, 120),
      description: report.description,
      definition: report.definition,
    });
    if (tab === "library") {
      navigate(`/reports/builder/${copy.id}/edit`);
    } else {
      setReports((current) => [copy, ...current]);
    }
  });

  const toggleShare = (report: SavedReport) => act(async () => {
    const updated = await shareReport(report.id, !report.shared);
    setReports((current) => current.map((item) => (item.id === updated.id ? updated : item)));
  });

  const confirmDelete = () => act(async () => {
    if (!toDelete) return;
    const target = toDelete;
    setToDelete(null);
    await deleteReport(target.id);
    setReports((current) => current.filter((report) => report.id !== target.id));
  });

  const tabButton = (key: Tab, label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === key}
      onClick={() => setSearchParams(key === "library" ? { tab: "library" } : {})}
      className={`rounded-full px-4 py-2 text-sm font-bold transition ${tab === key
        ? "bg-indigo-600 text-white dark:bg-indigo-500"
        : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/10"}`}
    >
      {label}
    </button>
  );

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaTable}
        eyebrow={String(t("reports.title", { defaultValue: "Reports" }))}
        title={String(canCreate
          ? t("reports.builder.title", { defaultValue: "Report builder" })
          : t("reports.builder.library", { defaultValue: "Library" }))}
        description={String(canCreate
          ? t("reports.builder.description", {
            defaultValue: "Compose your own reports from families, parents, children and classes.",
          })
          : t("reports.builder.library_description", {
            defaultValue: "Open the reports colleagues shared. You see only the data you have access to yourself.",
          }))}
        actions={canCreate ? (
          <button type="button" className={primaryButton} onClick={() => navigate("/reports/builder/new")}>
            <FaPlus aria-hidden /> {t("reports.builder.new", { defaultValue: "New report" })}
          </button>
        ) : undefined}
      />

      {canCreate && (
        <div role="tablist" className="flex flex-wrap gap-2">
          {tabButton("mine", String(t("reports.builder.my_reports", { defaultValue: "My reports" })))}
          {tabButton("library", String(t("reports.builder.library", { defaultValue: "Library" })))}
        </div>
      )}
      {tab === "library" && (
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {t("reports.builder.library_hint", {
            defaultValue: "Reports colleagues shared. You always see only the data you have access to yourself.",
          })}
        </p>
      )}

      {error && (
        <p role="alert" className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-400/30 dark:bg-rose-400/10 dark:text-rose-100">
          {error}
        </p>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : reports.length === 0 ? (
        <LdsOverviewEmpty>
          {tab === "library"
            ? t("reports.builder.empty_library", { defaultValue: "No reports have been shared yet." })
            : canCreate
              ? t("reports.builder.empty_list", { defaultValue: "You have no reports yet. Choose New report to make your first." })
              : t("reports.builder.empty_list_view", { defaultValue: "You have no reports yet." })}
        </LdsOverviewEmpty>
      ) : (
        <LdsTableCard className={fitWidth}>
          <LdsTableScroll className="p-3">
            <LdsTable>
              <LdsTableHead>
                <LdsTableRow>
                  <LdsTableHeadCell>{t("reports.builder.name", { defaultValue: "Name" })}</LdsTableHeadCell>
                  <LdsTableHeadCell>{t("reports.builder.subject", { defaultValue: "One row per" })}</LdsTableHeadCell>
                  {tab === "library" && (
                    <LdsTableHeadCell>{t("reports.builder.maker", { defaultValue: "Made by" })}</LdsTableHeadCell>
                  )}
                  <LdsTableHeadCell>{t("reports.builder.updated", { defaultValue: "Last changed" })}</LdsTableHeadCell>
                  <LdsTableHeadCell>
                    <span className="sr-only">{t("reports.builder.actions", { defaultValue: "Actions" })}</span>
                  </LdsTableHeadCell>
                </LdsTableRow>
              </LdsTableHead>
              <LdsTableBody>
                {reports.map((report) => (
                  <LdsTableRow key={report.id}>
                    <LdsTableCell>
                      <button type="button" className="text-left font-bold text-indigo-700 hover:underline dark:text-indigo-300"
                              onClick={() => navigate(`/reports/builder/${report.id}`)}>
                        {report.name}
                      </button>
                      {tab === "mine" && report.shared && (
                        <LdsBadge className="ml-2">{t("reports.builder.shared_badge", { defaultValue: "Shared" })}</LdsBadge>
                      )}
                      {report.description && (
                        <p className="text-xs text-slate-500 dark:text-slate-400">{report.description}</p>
                      )}
                    </LdsTableCell>
                    <LdsTableCell>{sourceName(report.source)}</LdsTableCell>
                    {tab === "library" && (
                      <LdsTableCell>
                        {report.owned
                          ? t("reports.builder.you", { defaultValue: "You" })
                          : report.ownerName || "—"}
                      </LdsTableCell>
                    )}
                    <LdsTableCell>{formatDutchDate(report.updatedAt)}</LdsTableCell>
                    <LdsTableCell>
                      <div className="flex flex-wrap justify-end gap-1">
                        <button type="button" className={ghostButton} onClick={() => navigate(`/reports/builder/${report.id}`)}>
                          <FaFolderOpen aria-hidden /> {t("reports.builder.open", { defaultValue: "Open" })}
                        </button>
                        {canCreate && report.owned && (
                          <button type="button" className={ghostButton}
                                  onClick={() => navigate(`/reports/builder/${report.id}/edit`)}>
                            <FaEdit aria-hidden /> {t("reports.builder.edit", { defaultValue: "Edit" })}
                          </button>
                        )}
                        {canCreate && (
                          <button type="button" className={ghostButton} disabled={busy} onClick={() => duplicate(report)}>
                            <FaCopy aria-hidden /> {report.owned
                              ? t("reports.builder.duplicate", { defaultValue: "Duplicate" })
                              : t("reports.builder.make_copy", { defaultValue: "Make a copy" })}
                          </button>
                        )}
                        {canShare && report.owned && (
                          <button type="button" className={ghostButton} disabled={busy} onClick={() => toggleShare(report)}>
                            <FaShareAlt aria-hidden /> {report.shared
                              ? t("reports.builder.unshare", { defaultValue: "Stop sharing" })
                              : t("reports.builder.share", { defaultValue: "Share in library" })}
                          </button>
                        )}
                        {canCreate && report.owned && (
                          <button type="button" className={`${ghostButton} text-rose-600 dark:text-rose-300`}
                                  onClick={() => setToDelete(report)}>
                            <FaTrash aria-hidden /> {t("reports.builder.delete", { defaultValue: "Delete" })}
                          </button>
                        )}
                      </div>
                    </LdsTableCell>
                  </LdsTableRow>
                ))}
              </LdsTableBody>
            </LdsTable>
          </LdsTableScroll>
        </LdsTableCard>
      )}

      <Modal
        show={Boolean(toDelete)}
        type="confirm"
        title={t("reports.builder.delete_title", { defaultValue: "Delete report?" })}
        message={t(toDelete?.shared ? "reports.builder.delete_message_shared" : "reports.builder.delete_message", {
          name: toDelete?.name ?? "",
          defaultValue: toDelete?.shared
            ? "“{{name}}” will be deleted, also from the library. This cannot be undone. The data itself is not touched."
            : "“{{name}}” will be deleted. This cannot be undone. The data itself is not touched.",
        })}
        onClose={() => setToDelete(null)}
        footer={(
          <div className="flex justify-end gap-2">
            <button type="button" className={ghostButton} onClick={() => setToDelete(null)}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </button>
            <button type="button" className={dangerButton} disabled={busy} onClick={confirmDelete}>
              {t("reports.builder.delete", { defaultValue: "Delete" })}
            </button>
          </div>
        )}
      />
    </LdsOverviewPage>
  );
}
